var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, useCallback, useEffect, useRef, useState, } from 'react';
import { flip, offset, shift, useFloating, } from '@floating-ui/react-dom';
import clsx from 'clsx';
import { useDocument } from '../hooks/useDocument.js';
import { Portal } from '../Portal/index.js';
import { FloatingFullContext, useUpdateOnScroll, } from '../utils/floating/index.js';
import { findFirstFocusableNodeIncludingDisabled } from '../utils/focus.js';
import { useUniqueId } from '../utils/unique-id.js';
import { PopoverContext, usePopover } from './PopoverContext.js';
import { PopoverPanel } from './PopoverPanel.js';
import { PopoverCloseSource, } from './types.js';
import { setActivatorAttributes } from './utils.activator.js';
export const Popover = (_a) => {
    var { children, padding = 'md', placement = 'bottom', activator, activatorWrapper = 'div', wrapperClassName = '', open, onClose, preventFocusOnClose, fixed } = _a, rest = __rest(_a, ["children", "padding", "placement", "activator", "activatorWrapper", "wrapperClassName", "open", "onClose", "preventFocusOnClose", "fixed"]);
    const { document } = useDocument();
    const [activatorNode, setActivatorNode] = useState();
    const activatorContainer = useRef(null);
    const WrapperComponent = activatorWrapper;
    const id = useUniqueId('popover');
    const hideTimer = useRef();
    const showTimer = useRef();
    useEffect(() => () => clearTimeout(hideTimer.current), []);
    useEffect(() => () => clearTimeout(showTimer.current), []);
    const { x, y, reference, floating, strategy, refs, update } = useFloating({
        placement,
        strategy: fixed ? 'fixed' : undefined,
        middleware: [offset(8), shift({ padding: 16 }), flip()],
    });
    useUpdateOnScroll(refs.reference, update, 'scroll');
    useUpdateOnScroll(refs.reference, update, 'resize');
    const setAccessibilityAttributes = useCallback(() => {
        if (activatorContainer.current == null) {
            return;
        }
        const firstFocusable = findFirstFocusableNodeIncludingDisabled(document, activatorContainer.current);
        const focusableActivator = firstFocusable || activatorContainer.current;
        const activatorDisabled = 'disabled' in focusableActivator && Boolean(focusableActivator.disabled);
        setActivatorAttributes(focusableActivator, {
            id,
            active: open,
            activatorDisabled,
            ariaHaspopup: 'true',
        });
    }, [id, open]);
    const handleClose = (source) => {
        onClose(source);
        if (activatorContainer.current == null || preventFocusOnClose) {
            return;
        }
        if ((source === PopoverCloseSource.FocusOut ||
            source === PopoverCloseSource.EscapeKeypress) &&
            activatorNode) {
            const focusableActivator = findFirstFocusableNodeIncludingDisabled(document, activatorNode) ||
                findFirstFocusableNodeIncludingDisabled(document, activatorContainer.current) ||
                activatorContainer.current;
            focusableActivator.focus();
        }
    };
    useEffect(() => {
        if (!activatorNode && activatorContainer.current) {
            setActivatorNode(activatorContainer.current.firstElementChild);
        }
        else if (activatorNode &&
            activatorContainer.current &&
            !activatorContainer.current.contains(activatorNode)) {
            setActivatorNode(activatorContainer.current.firstElementChild);
        }
        setAccessibilityAttributes();
    }, [activatorNode, setAccessibilityAttributes]);
    useEffect(() => {
        if (activatorNode && activatorContainer.current) {
            const element = activatorContainer.current
                .firstElementChild;
            setActivatorNode(element);
            reference(element);
        }
        setAccessibilityAttributes();
    }, [activatorNode, setAccessibilityAttributes]);
    const portal = activatorNode ? (React.createElement(Portal, { idPrefix: "popover" },
        React.createElement(PopoverPanel, Object.assign({ id: id, activator: activatorNode, open: open, onClose: handleClose }, rest), children))) : null;
    return (React.createElement(PopoverContext.Provider, { value: {
            padding,
        } },
        React.createElement(FloatingFullContext.Provider, { value: {
                strategy,
                x,
                y,
                reference,
                floating,
                update,
            } },
            React.createElement(WrapperComponent, { ref: activatorContainer, className: wrapperClassName },
                Children.only(activator),
                portal))));
};
const PopoverContent = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { padding } = usePopover();
    return (React.createElement("div", Object.assign({ className: clsx([
            'relative',
            padding === 'sm' && 'px-2 py-3 sm:px-3',
            padding === 'md' && 'px-4 py-5 sm:p-6 ',
            padding === 'lg' && 'px-5 py-6 sm:p-8',
            className,
        ]) }, rest), children));
};
const PopoverFooter = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { padding } = usePopover();
    return (React.createElement("div", Object.assign({ className: clsx([
            'bg-surface-subdued',
            padding === 'sm' && 'px-2 py-3 sm:px-3',
            padding === 'md' && 'px-4 py-5 sm:p-6 ',
            padding === 'lg' && 'px-5 py-5 sm:px-8',
            className,
        ]) }, rest), children));
};
Popover.Content = PopoverContent;
Popover.Footer = PopoverFooter;
//# sourceMappingURL=Popover.js.map